import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { LoadingShell } from '@/components/ui-bits';
import { fmtRelative, fmtDate, initials } from '@/lib/format';
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  Send,
  CheckSquare,
  NotebookPen,
  Activity,
  Trash2,
  FileText,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/api';

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);

  const [tab, setTab] = useState('timeline');
  const [timeline, setTimeline] = useState([]);

  const [note, setNote] = useState('');
  const [waBody, setWaBody] = useState('');
  const [taskTitle, setTaskTitle] = useState('');

  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [taskDue, setTaskDue] = useState('');
  const [taskRemindH, setTaskRemindH] = useState(1);
  const [lossReasons, setLossReasons] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [layout, setLayout] = useState(null);
  const [leadLists, setLeadLists] = useState([]);
  const [listBusy, setListBusy] = useState(false);

  const fieldHidden = (key) => !!(layout?.system_fields?.[key]?.hidden);
  const fieldReadonly = (key) => !!(layout?.system_fields?.[key]?.readonly);
  const allowedTabs = layout?.tabs || ['timeline', 'notes', 'tasks', 'whatsapp', 'email', 'checklist'];

  const load = async () => {
    const { data } = await api.get(`/leads/${id}`);
    setData(data);
    setChecklists(data.lead?.checklists || []);
    setWaBody(
      'Hola (nombre_cliente), te saluda (nombre_empleado) de (nombre_empresa).'
    );
    // Load stages for this lead's pipeline
    const pid = data.lead?.pipeline_id;
    try {
      const qs = pid ? `?pipeline_id=${pid}` : '';
      const { data: p } = await api.get(`/pipeline${qs}`);
      setStages((p.pipeline?.stages || []).sort((a, b) => a.order - b.order));
    } catch {
      /* ignore */
    }
    try {
      const { data: tl } = await api.get(`/leads/${id}/timeline?limit=150`);
      setTimeline(tl.timeline || []);
    } catch {
      setTimeline([]);
    }
    try {
      const { data: cl } = await api.get(`/leads/${id}/checklists`);
      setChecklists(cl.checklists || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    api.get('/pipeline/list').then((r) => setPipelines(r.data.pipelines || [])).catch(() => {});
    api.get('/admin/users').then((r) => setUsers(r.data.users)).catch(() => {});
    api.get('/whatsapp/templates').then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get('/email/templates').then((r) => setEmailTemplates(r.data.templates)).catch(() => {});
    api.get('/companies?limit=500').then((r) => setCompanies(r.data.companies || [])).catch(() => {});
    api.get('/note-templates').then((r) => setNoteTemplates(r.data.templates || [])).catch(() => {});
    api.get('/catalogs?kind=loss_reason').then((r) => setLossReasons(r.data.items || [])).catch(() => {});
    api.get('/lead-lists?active_only=true').then((r) => setLeadLists(r.data.lists || [])).catch(() => {});
    api.get('/admin/layout').then((r) => {
      setLayout(r.data.layout || null);
      const tabs = r.data.layout?.tabs || [];
      if (tabs.length && !tabs.includes(tab)) setTab(tabs[0]);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!data) return <LoadingShell />;

  const lead = data.lead;
  const stage = stages.find((s) => s.id === lead.stage_id);
  const assignee = users.find((u) => u.id === lead.assigned_to);

  const updateField = async (patch) => {
    try {
      await api.patch(`/leads/${id}`, patch);
      toast.success('Actualizado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const sendWa = async () => {
    if (!waBody.trim()) return;
    try {
      const { data } = await api.post('/whatsapp/send', { lead_id: id, body: waBody });
      if (data.result.fallback && data.result.wa_link) {
        window.open(data.result.wa_link, '_blank');
        toast.warning('No se envió por la sesión QR', {
          description: data.result.error
            || 'WhatsApp QR desconectado. Revisa Ajustes → WhatsApp o usa la pestaña de WhatsApp Web que se abrió.',
        });
      } else if (data.result.provider === 'qr') {
        toast.success('Enviado por WhatsApp (QR)');
      } else {
        toast.success('Enviado por WhatsApp Cloud API');
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const sendTemplate = async (tplId) => {
    try {
      const { data } = await api.post('/whatsapp/send', { lead_id: id, template_id: tplId });
      if (data.result.fallback && data.result.wa_link) window.open(data.result.wa_link, '_blank');
      toast.success('Mensaje enviado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  // ---- Email composer ----
  const pickEmailTemplate = (tplId) => {
    setEmailTemplateId(tplId);
    if (!tplId) {
      setEmailSubject('');
      setEmailBody('');
      return;
    }
    const tpl = emailTemplates.find((t) => t.id === tplId);
    if (tpl) {
      setEmailSubject(tpl.subject || '');
      setEmailBody(tpl.body_html || '');
    }
  };

  const sendEmail = async () => {
    if (!lead.email) {
      toast.error('El lead no tiene email');
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Falta asunto o cuerpo');
      return;
    }
    setSendingEmail(true);
    try {
      // We send the edited subject/body explicitly so the user's edits win,
      // even when a template was originally selected.
      const { data } = await api.post('/email/send', {
        lead_id: id,
        subject: emailSubject,
        body_html: emailBody,
      });
      if (data?.result?.sent) {
        toast.success('Email enviado');
      } else {
        toast.warning('Email no enviado', { description: data?.result?.reason || 'Revisa la configuración SMTP' });
      }
      setEmailSubject('');
      setEmailBody('');
      setEmailTemplateId('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSendingEmail(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data: res } = await api.post(`/leads/${id}/notes`, { body: note });
      setNote('');
      const n = (res.mentioned || []).length;
      toast.success(n ? `Nota agregada · ${n} mención(es)` : 'Nota agregada');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const createDealFromLead = async () => {
    try {
      const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Deal';
      const { data } = await api.post('/deals', {
        name: `Oportunidad: ${name}`,
        lead_id: id,
        company_id: lead.company_id || null,
        amount: lead.deal_value || 0,
        stage: 'open',
        probability: 50,
        currency: lead.currency || 'MXN',
      });
      toast.success('Deal creado', { description: data.deal?.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear deal');
    }
  };

  const insertMention = (u) => {
    const token = `@${(u.email || '').split('@')[0] || u.name?.split(' ')[0] || 'user'}`;
    setNote((prev) => {
      const at = prev.lastIndexOf('@');
      if (at >= 0) return `${prev.slice(0, at)}${token} `;
      return `${prev}${token} `;
    });
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    try {
      await api.post('/tasks', {
        lead_id: id,
        title: taskTitle,
        type: 'follow_up',
        priority: 'normal',
        due_at: taskDue ? new Date(taskDue).toISOString() : null,
        remind_before_hours: Number(taskRemindH) || 1,
        email_reminder: true,
      });
      setTaskTitle('');
      setTaskDue('');
      toast.success('Tarea creada (recordatorio por email activo)');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const applyNoteTemplate = (tplId) => {
    const tpl = noteTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    if (tpl.category === 'checklist' || (tpl.items || []).length) {
      // create checklist from template
      api.post(`/leads/${id}/checklists`, {
        title: tpl.name,
        template_id: tpl.id,
        items: tpl.items || [],
      }).then(() => {
        toast.success('Checklist creado');
        setTab('checklist');
        load();
      }).catch((err) => toast.error(err?.response?.data?.detail || 'Error'));
      return;
    }
    setNote(tpl.body || '');
  };

  const toggleCheckItem = async (clId, itemId, done) => {
    try {
      const { data } = await api.patch(`/leads/${id}/checklists/${clId}/items/${itemId}`, { done });
      setChecklists(data.checklists || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const addChecklist = async () => {
    const title = prompt('Título del checklist', 'Checklist');
    if (!title) return;
    try {
      await api.post(`/leads/${id}/checklists`, { title, items: ['Paso 1', 'Paso 2'] });
      toast.success('Checklist creado');
      setTab('checklist');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const seedTemplates = async () => {
    try {
      const { data } = await api.post('/note-templates/seed-defaults');
      toast.success(data.created ? `${data.created} plantillas creadas` : (data.message || 'OK'));
      const r = await api.get('/note-templates');
      setNoteTemplates(r.data.templates || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const deleteLead = async () => {
    if (!confirm(`¿Eliminar este lead de forma permanente?\n\nSe borrarán también sus notas, tareas, mensajes y actividades. Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${id}`);
      toast.success('Lead eliminado');
      navigate('/app/leads');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
      setDeleting(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const tok = localStorage.getItem('zunoid_access');
      const res = await fetch(`${API_BASE}/leads/${id}/pdf`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!res.ok) throw new Error('fail');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lead_${(lead.first_name || 'ficha').replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch {
      toast.error('No se pudo generar el PDF');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Link to="/app/leads" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Volver a leads
        </Link>
        <div className="flex items-center gap-1">
          <button type="button" className="btn-ghost h-9 text-xs" onClick={downloadPdf} title="Exportar ficha PDF">
            <Download className="h-4 w-4" /> PDF
          </button>
          <button
            data-testid="lead-delete-btn"
            onClick={deleteLead}
            disabled={deleting}
            className="btn-ghost text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10"
          >
            <Trash2 className="h-4 w-4" />
            {deleting ? 'Eliminando…' : 'Eliminar lead'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-4">
          <div className="surface p-5">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-full bg-accent grid place-items-center text-lg font-semibold">
                {initials(`${lead.first_name} ${lead.last_name}`)}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-2xl font-semibold tracking-tight">
                  {lead.first_name} {lead.last_name}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {lead.phone_e164 && (<span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone_e164}</span>)}
                  {lead.email && (<span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>)}
                  {lead.city && (<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.city}</span>)}
                  <span>Creado {fmtRelative(lead.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {lead.phone_e164 && (<a href={`tel:${lead.phone_e164}`} className="btn-ghost"><Phone className="h-4 w-4" />Llamar</a>)}
                {lead.phone_e164 && (<a href={`sms:${lead.phone_e164}`} className="btn-ghost"><MessageCircle className="h-4 w-4" />SMS</a>)}
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {!fieldHidden('pipeline_id') && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Pipeline</label>
                  <select
                    value={lead.pipeline_id || ''}
                    disabled={fieldReadonly('pipeline_id')}
                    onChange={async (e) => {
                      const pid = e.target.value || null;
                      try {
                        const qs = pid ? `?pipeline_id=${pid}` : '';
                        const { data: p } = await api.get(`/pipeline${qs}`);
                        const first = (p.pipeline?.stages || []).sort((a, b) => a.order - b.order)[0];
                        await updateField({ pipeline_id: pid, stage_id: first?.id || lead.stage_id });
                        setStages((p.pipeline?.stages || []).sort((a, b) => a.order - b.order));
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Error');
                      }
                    }}
                    className="input mt-1"
                  >
                    {pipelines.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' ★' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              {!fieldHidden('stage_id') && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Etapa</label>
                  <select value={lead.stage_id || ''} disabled={fieldReadonly('stage_id')} onChange={(e) => updateField({ stage_id: e.target.value })} className="input mt-1">
                    {stages.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
              )}
              {!fieldHidden('assigned_to') && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Asignado a</label>
                  <select value={lead.assigned_to || ''} disabled={fieldReadonly('assigned_to')} onChange={(e) => updateField({ assigned_to: e.target.value || null })} className="input mt-1">
                    <option value="">Sin asignar</option>
                    {users.map((u) => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}
                  </select>
                </div>
              )}
              {!fieldHidden('status') && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Estado</label>
                  <select
                    value={lead.status || 'open'}
                    disabled={fieldReadonly('status')}
                    onChange={(e) => {
                      const st = e.target.value;
                      if (st === 'lost' && lossReasons.length && !lead.lost_reason) {
                        updateField({ status: st });
                        return;
                      }
                      updateField({ status: st, ...(st !== 'lost' ? { lost_reason: null } : {}) });
                    }}
                    className="input mt-1"
                  >
                    <option value="open">Abierto</option>
                    <option value="closed">Cerrado</option>
                    <option value="won">Ganado</option>
                    <option value="lost">Perdido</option>
                  </select>
                </div>
              )}
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {!fieldHidden('deal_value') && layout?.show_deal_value !== false && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Valor deal (MXN)</label>
                  <input
                    type="number"
                    className="input mt-1"
                    disabled={fieldReadonly('deal_value')}
                    value={lead.deal_value ?? ''}
                    onChange={(e) => updateField({ deal_value: e.target.value === '' ? null : Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
              )}
              {!fieldHidden('company') && layout?.show_company !== false && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Empresa</label>
                  <select
                    className="input mt-1"
                    value={lead.company_id || ''}
                    onChange={(e) => {
                      const cid = e.target.value || null;
                      const co = companies.find((x) => x.id === cid);
                      updateField({ company_id: cid, company_name: co?.name || lead.company_name || null });
                    }}
                  >
                    <option value="">— Sin empresa —</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {!lead.company_id && (
                    <input
                      className="input mt-1 text-xs"
                      placeholder="O escribe nombre libre"
                      value={lead.company_name || ''}
                      onChange={(e) => updateField({ company_name: e.target.value || null })}
                    />
                  )}
                </div>
              )}
              {!fieldHidden('lost_reason') && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Motivo pérdida</label>
                  {lossReasons.length > 0 ? (
                    <select
                      className="input mt-1"
                      value={lead.lost_reason || ''}
                      onChange={(e) => updateField({
                        lost_reason: e.target.value || null,
                        ...(e.target.value ? { status: 'lost' } : {}),
                      })}
                      disabled={lead.status !== 'lost' && !lead.lost_reason}
                    >
                      <option value="">— Selecciona —</option>
                      {lossReasons.map((r) => (
                        <option key={r.id} value={r.value || r.label}>{r.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input mt-1"
                      value={lead.lost_reason || ''}
                      onChange={(e) => updateField({ lost_reason: e.target.value || null })}
                      placeholder="Precio, no contesta…"
                      disabled={lead.status !== 'lost'}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!lead.marketing_opt_out}
                  onChange={(e) => updateField({ marketing_opt_out: e.target.checked })}
                />
                Opt-out marketing (no campañas)
              </label>
              <button type="button" className="btn-ghost h-8 text-xs" onClick={createDealFromLead}>
                + Crear deal
              </button>
              <Link to="/app/deals" className="text-xs text-primary hover:underline">Ver deals</Link>
            </div>
          </div>

          <div className="surface">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-sm flex-wrap">
              {[
                ['timeline', 'Timeline', Activity, 'timeline'],
                ['activity', 'Actividad', Activity, 'timeline'],
                ['messages', 'Mensajes', MessageCircle, 'whatsapp'],
                ['notes', 'Notas', NotebookPen, 'notes'],
                ['checklist', 'Checklist', CheckSquare, 'checklist'],
                ['tasks', 'Tareas', CheckSquare, 'tasks'],
              ]
                .filter(([, , , layoutKey]) => {
                  if (!layout) return true;
                  if (layoutKey === 'whatsapp') {
                    return allowedTabs.includes('whatsapp') || allowedTabs.includes('email') || allowedTabs.includes('timeline');
                  }
                  return allowedTabs.includes(layoutKey);
                })
                .map(([k, label, Icon]) => (
                  <button key={k} type="button" onClick={() => setTab(k)} className={`btn-ghost ${tab === k ? 'bg-accent text-foreground' : ''}`}>
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
            </div>

            <div className="p-4">
              {tab === 'timeline' && (
                <div className="space-y-2 max-h-[480px] overflow-y-auto">
                  {timeline.length === 0 && <div className="text-sm text-muted-foreground">Sin eventos aún.</div>}
                  {timeline.map((ev) => (
                    <div key={`${ev.kind}-${ev.id}-${ev.at}`} className="flex items-start gap-3 py-2 border-b border-border/40">
                      <div className={`h-2 w-2 rounded-full mt-2 shrink-0 ${
                        ev.kind === 'message' ? 'bg-primary'
                          : ev.kind === 'campaign' ? 'bg-[hsl(var(--chart-2))]'
                            : ev.kind === 'task' ? 'bg-[hsl(var(--warning))]'
                              : 'bg-muted-foreground'
                      }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{ev.kind} · {ev.type}</div>
                        <div className="text-sm whitespace-pre-wrap break-words">{ev.title}</div>
                        <div className="text-xs text-muted-foreground">{fmtDate(ev.at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'activity' && (
                <div className="space-y-2">
                  {data.activities.length === 0 && (<div className="text-sm text-muted-foreground">Sin actividad aún.</div>)}
                  {data.activities.map((a) => (
                    <div key={a.id} className="flex items-start gap-3 py-2">
                      <div className="h-2 w-2 rounded-full bg-primary mt-2 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{a.message}</div>
                        <div className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'messages' && (
                <div className="space-y-2">
                  {(data.messages || []).length === 0 && (<div className="text-sm text-muted-foreground">Sin mensajes aún.</div>)}
                  {(data.messages || []).map((m) => (
                    <div key={m.id} className="flex items-start gap-3 py-2 border-b border-border/60">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground">
                          {m.channel} · {m.direction || 'out'} · {fmtRelative(m.created_at)}
                        </div>
                        <div className="text-sm whitespace-pre-wrap break-words">{m.body || m.subject || ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'notes' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      className="input w-auto text-xs h-9"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) applyNoteTemplate(e.target.value);
                        e.target.value = '';
                      }}
                    >
                      <option value="">Plantilla de nota…</option>
                      {noteTemplates.filter((t) => t.enabled !== false).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.category === 'checklist' || (t.items || []).length ? ' ✓' : ''}
                        </option>
                      ))}
                    </select>
                    {noteTemplates.length === 0 && (
                      <button type="button" className="btn-ghost h-8 text-xs" onClick={seedTemplates}>
                        Crear plantillas base
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Nota… usa @usuario para mencionar"
                      className="input flex-1 min-h-[72px]"
                    />
                    <button onClick={addNote} className="btn-primary self-end">Agregar</button>
                  </div>
                  {note.includes('@') && (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[10px] text-muted-foreground w-full">Mencionar:</span>
                      {users.slice(0, 12).map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className="chip text-[11px] hover:bg-accent"
                          onClick={() => insertMention(u)}
                        >
                          @{(u.email || '').split('@')[0] || u.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground">Ej: @juan revisa este lead — notifica al usuario si coincide email/nombre.</p>
                  {data.notes.map((n) => (
                    <div key={n.id} className="border-l-2 border-primary/40 pl-3 py-1">
                      <div className="text-sm whitespace-pre-wrap">
                        {(n.body || '').split(/(@[a-zA-Z0-9_.+\-]+)/g).map((part, i) =>
                          part.startsWith('@') ? (
                            <span key={i} className="text-primary font-medium">{part}</span>
                          ) : (
                            <span key={i}>{part}</span>
                          ),
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {n.author_name || ''} · {fmtRelative(n.created_at)}
                        {(n.mentioned_user_ids || []).length > 0 && ` · ${n.mentioned_user_ids.length} mención(es)`}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'checklist' && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-primary h-8 text-xs" onClick={addChecklist}>+ Checklist</button>
                    <select
                      className="input w-auto text-xs h-8"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) applyNoteTemplate(e.target.value);
                        e.target.value = '';
                      }}
                    >
                      <option value="">Desde plantilla…</option>
                      {noteTemplates
                        .filter((t) => t.category === 'checklist' || (t.items || []).length)
                        .map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                    {noteTemplates.length === 0 && (
                      <button type="button" className="btn-ghost h-8 text-xs" onClick={seedTemplates}>
                        Plantillas base
                      </button>
                    )}
                  </div>
                  {checklists.length === 0 && (
                    <p className="text-sm text-muted-foreground">Sin checklists. Crea uno o usa una plantilla.</p>
                  )}
                  {checklists.map((cl) => {
                    const done = (cl.items || []).filter((i) => i.done).length;
                    const total = (cl.items || []).length || 1;
                    const pct = Math.round((done / total) * 100);
                    return (
                      <div key={cl.id} className="border border-border rounded-[12px] p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-sm">{cl.title}</div>
                          <span className="text-[11px] text-muted-foreground">{done}/{total} · {pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-secondary mb-2 overflow-hidden">
                          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="space-y-1.5">
                          {(cl.items || []).map((it) => (
                            <label key={it.id} className="flex items-start gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={!!it.done}
                                onChange={(e) => toggleCheckItem(cl.id, it.id, e.target.checked)}
                              />
                              <span className={it.done ? 'line-through text-muted-foreground' : ''}>{it.text}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn-ghost h-7 px-2 text-[11px] mt-2 text-[hsl(var(--danger))]"
                          onClick={() => {
                            if (!confirm('¿Eliminar checklist?')) return;
                            api.delete(`/leads/${id}/checklists/${cl.id}`).then(load).catch((err) => toast.error(err?.response?.data?.detail || 'Error'));
                          }}
                        >
                          Eliminar checklist
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'tasks' && (
                <div className="space-y-2">
                  <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2">
                    <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Nueva tarea…" className="input" />
                    <input type="datetime-local" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="input w-auto text-xs" title="Vencimiento" />
                    <select value={taskRemindH} onChange={(e) => setTaskRemindH(Number(e.target.value))} className="input w-auto text-xs" title="Recordar antes">
                      <option value={0}>Al vencer</option>
                      <option value={1}>1h antes</option>
                      <option value={3}>3h antes</option>
                      <option value={24}>1 día antes</option>
                    </select>
                    <button onClick={addTask} className="btn-primary">Crear</button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Se envía recordatorio por email + campana al asignado.</p>
                  {data.tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border/60">
                      <input
                        type="checkbox"
                        checked={t.status === 'done'}
                        onChange={(e) => api.patch(`/tasks/${t.id}`, { status: e.target.checked ? 'done' : 'open' }).then(load)}
                      />
                      <div className="flex-1">
                        <div className={`text-sm ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.type} · {t.priority} · vence {fmtRelative(t.due_at)}
                          {t.reminder_sent_at ? ' · recordatorio enviado' : t.email_reminder !== false ? ' · recordatorio OK' : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <aside className="space-y-4">
          {/* EMAIL COMPOSER */}
          {(!layout || allowedTabs.includes('email')) && (
          <div className="surface p-5">
            <h3 className="font-display font-semibold mb-3 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Enviar email
            </h3>

            {!lead.email ? (
              <div className="text-sm text-muted-foreground">El lead no tiene email.</div>
            ) : (
              <>
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Plantilla (opcional, puedes editarla antes de enviar)
                  </span>
                  <select
                    data-testid="email-template-select"
                    value={emailTemplateId}
                    onChange={(e) => pickEmailTemplate(e.target.value)}
                    className="input mt-1"
                  >
                    <option value="">— Sin plantilla (escribir libre) —</option>
                    {emailTemplates
                      .filter((t) => t.enabled !== false)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </label>

                <label className="block mt-3">
                  <span className="text-xs font-medium text-muted-foreground">Asunto</span>
                  <input
                    data-testid="email-subject-input"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Asunto del email"
                    className="input mt-1"
                  />
                </label>

                <label className="block mt-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    Cuerpo (admite HTML y variables como (nombre_cliente), (nombre_empleado), (nombre_empresa))
                  </span>
                  <textarea
                    data-testid="email-body-input"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Escribe el contenido del email…"
                    className="input mt-1 min-h-[180px] font-mono text-xs"
                  />
                </label>

                <button
                  data-testid="email-send-btn"
                  onClick={sendEmail}
                  disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                  className="btn-primary w-full mt-3"
                >
                  <Send className="h-4 w-4" />
                  {sendingEmail ? 'Enviando…' : 'Enviar email'}
                </button>
                <p className="text-[11px] text-muted-foreground mt-2">
                  La plantilla se carga al seleccionarla. Edítala libremente antes de enviar — tus cambios prevalecen sobre la plantilla original.
                </p>
              </>
            )}
          </div>
          )}

          {/* WHATSAPP */}
          {(!layout || allowedTabs.includes('whatsapp')) && (
          <div className="surface p-5">
            <h3 className="font-display font-semibold mb-3 flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Enviar WhatsApp
            </h3>
            {!lead.phone_e164 ? (
              <div className="text-sm text-muted-foreground">El lead no tiene teléfono.</div>
            ) : (
              <>
                <textarea value={waBody} onChange={(e) => setWaBody(e.target.value)} className="input min-h-[110px]" />
                <button onClick={sendWa} className="btn-primary w-full mt-2"><Send className="h-4 w-4" />Enviar</button>
                {templates.length > 0 && (
                  <div className="mt-3 border-t border-border pt-3">
                    <div className="text-xs text-muted-foreground mb-2">Plantillas rápidas</div>
                    <div className="flex flex-wrap gap-1">
                      {templates.filter((t) => t.enabled).map((t) => (
                        <button key={t.id} onClick={() => sendTemplate(t.id)} className="chip hover:bg-accent">
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          )}

          <div className="surface p-5">
            <h3 className="font-display font-semibold mb-2">Detalles</h3>
            <div className="text-sm space-y-2">
              {!fieldHidden('source') && <Row label="Origen" value={lead.source || '—'} />}
              {!fieldHidden('service') && <Row label="Servicio" value={lead.service || '—'} />}
              <Row label="Etapa" value={stage?.name || '—'} />
              <Row label="Asignado" value={assignee?.name || assignee?.email || 'Sin asignar'} />
              {!fieldHidden('tags') && <Row label="Tags" value={(lead.tags || []).join(', ') || '—'} />}
              <Row label="Última actividad" value={fmtRelative(lead.last_activity_at || lead.updated_at)} />
              {Object.keys(lead.custom_values || {}).length > 0 && (
                <div className="pt-2 mt-2 border-t border-border">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Campos personalizados</div>
                  {Object.entries(lead.custom_values)
                    .filter(([k]) => !(layout?.hidden_custom_fields || []).includes(k))
                    .map(([k, v]) => (
                      <Row
                        key={k}
                        label={(layout?.custom_fields || []).find((c) => c.key === k)?.label || k}
                        value={Array.isArray(v) ? v.join(', ') : v === true ? 'Sí' : v === false ? 'No' : String(v ?? '—')}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>

          {leadLists.length > 0 && (
            <div className="surface p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-semibold">Listas / carteras</h3>
                <Link to="/app/lead-lists" className="text-[11px] text-primary hover:underline">
                  Gestionar
                </Link>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Membresía manual. Las listas dinámicas también incluyen leads por filtro.
              </p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {leadLists.map((lst) => {
                  const member = (lead.list_ids || []).includes(lst.id);
                  return (
                    <label
                      key={lst.id}
                      className="flex items-center gap-2 text-xs rounded-lg border border-border/60 px-2 py-1.5 hover:bg-secondary/40"
                    >
                      <input
                        type="checkbox"
                        disabled={listBusy}
                        checked={member}
                        onChange={async (e) => {
                          const on = e.target.checked;
                          setListBusy(true);
                          try {
                            if (on) {
                              await api.post(`/lead-lists/${lst.id}/members`, { lead_ids: [lead.id] });
                              setData((prev) => ({
                                ...prev,
                                lead: {
                                  ...prev.lead,
                                  list_ids: [...new Set([...(prev.lead.list_ids || []), lst.id])],
                                },
                              }));
                              toast.success(`Agregado a ${lst.name}`);
                            } else {
                              await api.delete(`/lead-lists/${lst.id}/members`, {
                                data: { lead_ids: [lead.id] },
                              });
                              setData((prev) => ({
                                ...prev,
                                lead: {
                                  ...prev.lead,
                                  list_ids: (prev.lead.list_ids || []).filter((x) => x !== lst.id),
                                },
                              }));
                              toast.success(`Quitado de ${lst.name}`);
                            }
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'No se pudo actualizar la lista');
                          } finally {
                            setListBusy(false);
                          }
                        }}
                      />
                      <span
                        className="h-2 w-2 rounded-full shrink-0"
                        style={{ background: lst.color || '#0d9488' }}
                      />
                      <span className="truncate flex-1 font-medium">{lst.name}</span>
                      <Link
                        to={`/app/leads?list=${lst.id}`}
                        className="text-muted-foreground hover:text-primary shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        title="Ver lista"
                      >
                        →
                      </Link>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium truncate">{value}</span>
    </div>
  );
}
